import {
  Component,
  Input,
  Output,
  EventEmitter,
  Injector,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Options } from 'sortablejs';
import { FormBuilderManipulatorClass } from '../../../../core/utils/form-builder-manipulator-class.utils';
import { FormBuilderV2StateService } from '../../services/form-builder-v2-state.service';
import { FormBuilderService } from '../../../../core/services/form-builder.service';

@Component({
  selector: 'irembogov-fb-section',
  templateUrl: './fb-section.component.html',
  styleUrls: ['./fb-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbSectionComponent {
  @Input() section!: FormlyFieldConfig;
  @Input() sectionIndex!: number;
  @Input() selectedFieldKey: string | null = null;
  @Output() fieldSelected = new EventEmitter<FormlyFieldConfig>();
  @Output() sectionChanged = new EventEmitter<void>();
  @Output() deleteSection = new EventEmitter<number>();

  private formBuilderManipulator: FormBuilderManipulatorClass;
  private formBuilderService: FormBuilderService;

  blockSortableOptions: Options = {
    group: 'blocks',
    animation: 200,
    dragClass: 'fb-sortable-drag',
    ghostClass: 'fb-sortable-ghost',
    chosenClass: 'fb-sortable-chosen',
    onEnd: () => this.onBlockReorder(),
  };

  constructor(
    private injector: Injector,
    private stateService: FormBuilderV2StateService,
    private cdr: ChangeDetectorRef
  ) {
    this.formBuilderManipulator = new FormBuilderManipulatorClass(
      this.injector
    );
    this.formBuilderService = this.injector.get(FormBuilderService);
  }

  get sectionLabel(): string {
    return (
      (this.section?.props?.['label'] as string) ||
      `Section ${this.sectionIndex + 1}`
    );
  }

  get sectionId(): string {
    return (
      this.section?.id ||
      this.section?.key?.toString() ||
      `section-${this.sectionIndex}`
    );
  }

  get isCollapsed(): boolean {
    return this.stateService.isCollapsed(this.sectionId);
  }

  get blocks(): FormlyFieldConfig[] {
    return this.section?.fieldGroup || [];
  }

  get totalFieldCount(): number {
    return this.blocks.reduce(
      (sum, block) => sum + (block.fieldGroup?.length || 0),
      0
    );
  }

  toggleCollapse(): void {
    this.stateService.toggleCollapse(this.sectionId);
    this.cdr.markForCheck();
  }

  onSectionLabelChange(newLabel: string): void {
    if (this.section.props) {
      this.section.props['label'] = newLabel;
    }
    this.sectionChanged.emit();
  }

  onFieldSelected(field: FormlyFieldConfig): void {
    this.fieldSelected.emit(field);
  }

  // Open this section's settings in the properties panel (same wiring as
  // selecting a field): the by-type config resolves section settings.
  configure(): void {
    this.fieldSelected.emit(this.section);
  }

  onAddBlock(): void {
    const newBlock: FormlyFieldConfig = {
      key: `block_${Date.now().toString(36)}`,
      type: 'block',
      props: { label: 'New Block' },
      fieldGroup: [],
    };
    this.formBuilderManipulator.addNewBlockToFields(
      newBlock,
      this.sectionIndex
    );
    this.sectionChanged.emit();
    this.cdr.markForCheck();
  }

  onDeleteSection(): void {
    this.deleteSection.emit(this.sectionIndex);
  }

  onDeleteBlock(blockIndex: number): void {
    if (this.blocks.length <= 1) return; // Keep at least one block
    this.section.fieldGroup?.splice(blockIndex, 1);
    this.sectionChanged.emit();
    this.cdr.markForCheck();
  }

  onBlockChanged(): void {
    this.sectionChanged.emit();
  }

  trackByBlockKey(index: number, block: FormlyFieldConfig): string {
    return block.key?.toString() || block.id || `block-${index}`;
  }

  private onBlockReorder(): void {
    this.formBuilderService.requestFormlyFieldsSave();
    this.sectionChanged.emit();
  }
}
